import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
} from 'date-fns';
import {
  GET_MY_SCHEDULE,
  GET_MY_SCHEDULE_OVERRIDES,
  GET_MY_VACATIONS,
} from '../../graphql/my-availability-queries';
import {
  isCalendarDateInRange,
  isApprovedVacation,
  toCalendarDateKey,
} from '../../utils/availabilityDateUtils';
import { useMyPrivileges } from '../../hooks/useMyPrivileges';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildWeekDays = (referenceDate) => {
  const start = startOfWeek(referenceDate, { weekStartsOn: 0 });
  const end = endOfWeek(referenceDate, { weekStartsOn: 0 });
  return eachDayOfInterval({ start, end });
};

const findVacationForDay = (day, vacations) => {
  if (!Array.isArray(vacations) || vacations.length === 0) return null;
  return (
    vacations.find((entry) =>
      isApprovedVacation(entry)
      && isCalendarDateInRange(day, entry.startDate, entry.endDate)
    ) || null
  );
};

const toDayKey = (value) => {
  return toCalendarDateKey(value);
};

const getScheduleStatus = (day, schedulePattern, overridesByDate) => {
  const overrideKey = toDayKey(day);
  if (overrideKey && overridesByDate?.has(overrideKey)) {
    const override = overridesByDate.get(overrideKey);
    return {
      isWorking: override.isWorking === true,
      isRemote: override.isWorking === true && override.isRemote === true,
      isOverride: true,
      notes: override.notes || '',
    };
  }
  if (!Array.isArray(schedulePattern) || schedulePattern.length === 0) {
    return { isWorking: null, isRemote: false, isOverride: false, notes: '' };
  }
  const match = schedulePattern.find((entry) => entry.dayOfWeek === day.getDay());
  if (!match) return { isWorking: null, isRemote: false, isOverride: false, notes: '' };
  return {
    isWorking: match.isWorking,
    isRemote: match.isRemote === true,
    isOverride: false,
    notes: '',
  };
};

const formatVacationLabel = (vacationType) => {
  if (!vacationType) return 'Time Off';
  const cleaned = String(vacationType).replace(/_/g, ' ').toLowerCase();
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

function MyWeekScheduleWidget() {
  const { hasPrivilege, loading: privilegesLoading } = useMyPrivileges();
  const canShowWidget = hasPrivilege('TEAM_AVAILABILITY');
  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 0 }), [today]);
  const weekEnd = useMemo(() => endOfWeek(today, { weekStartsOn: 0 }), [today]);
  const weekDays = useMemo(() => buildWeekDays(today), [today]);

  const { data: scheduleData, loading: scheduleLoading } = useQuery(GET_MY_SCHEDULE, {
    skip: !canShowWidget,
    fetchPolicy: 'cache-and-network',
  });

  const { data: vacationsData, loading: vacationsLoading } = useQuery(GET_MY_VACATIONS, {
    variables: {
      startDate: weekStart.toISOString(),
      endDate: weekEnd.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
    skip: !canShowWidget,
  });

  const { data: overridesData } = useQuery(GET_MY_SCHEDULE_OVERRIDES, {
    variables: {
      startDate: weekStart.toISOString(),
      endDate: weekEnd.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
    skip: !canShowWidget,
  });

  const schedulePattern = scheduleData?.mySchedule || [];
  const vacations = vacationsData?.myVacations || [];
  const overrides = overridesData?.myScheduleOverrides || [];
  const overridesByDate = useMemo(() => {
    const map = new Map();
    overrides.forEach((entry) => {
      const key = toDayKey(entry.date);
      if (key) map.set(key, entry);
    });
    return map;
  }, [overrides]);
  const isLoading = scheduleLoading && !scheduleData;

  const { workingDayCount, remoteDayCount } = useMemo(() => {
    return weekDays.reduce(
      (acc, day) => {
        const vacation = findVacationForDay(day, vacations);
        if (vacation) return acc;
        const { isWorking, isRemote } = getScheduleStatus(day, schedulePattern, overridesByDate);
        if (isWorking !== true) return acc;
        return {
          workingDayCount: acc.workingDayCount + 1,
          remoteDayCount: acc.remoteDayCount + (isRemote ? 1 : 0),
        };
      },
      { workingDayCount: 0, remoteDayCount: 0 }
    );
  }, [weekDays, schedulePattern, vacations, overridesByDate]);

  if (privilegesLoading || !canShowWidget) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="bg-sky-100 p-2 rounded-lg">
              <svg
                className="w-5 h-5 text-sky-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">This Week's Schedule</h2>
              <p className="text-xs text-gray-500">
                {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                {!isLoading && (
                  <span className="ml-2 text-gray-400">
                    · {workingCountLabel(workingDayCount)}
                    {remoteDayCount > 0 && ` (${remoteDayCount} remote)`}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <Link
          to="/dashboard/my-availability"
          className="inline-flex items-center justify-center px-3 py-2 border border-sky-600 text-xs font-medium rounded-md text-sky-600 bg-white hover:bg-sky-50 transition-colors"
        >
          Update My Schedule
          <svg
            className="w-3 h-3 ml-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-gray-500">Loading your schedule…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const dayIndex = day.getDay();
            const vacation = findVacationForDay(day, vacations);
            const {
              isWorking: scheduleIsWorking,
              isRemote: scheduleIsRemote,
              isOverride: scheduleIsOverride,
              notes: scheduleNotes,
            } = getScheduleStatus(day, schedulePattern, overridesByDate);
            const isToday = day.toDateString() === today.toDateString();
            const isVacation = Boolean(vacation);
            const isWorking = !isVacation && scheduleIsWorking === true;
            const isRemote = isWorking && scheduleIsRemote === true;
            const isOff = !isVacation && scheduleIsWorking === false;
            const isOverride = !isVacation && scheduleIsOverride === true;
            const note = !isVacation && scheduleNotes ? scheduleNotes.trim() : '';
            const hasNote = note.length > 0;
            const cellClass = isVacation
              ? 'bg-amber-50 border-amber-200'
              : isRemote
              ? 'bg-purple-50 border-purple-200'
              : isWorking
              ? 'bg-sky-50 border-sky-200'
              : isOff
              ? 'bg-gray-50 border-gray-200'
              : 'bg-white border-dashed border-gray-300';
            const statusLabel = isVacation
              ? formatVacationLabel(vacation.vacationType)
              : isRemote
              ? 'Remote'
              : isWorking
              ? 'On-Island'
              : isOff
              ? 'Off'
              : 'Not set';
            const statusTextClass = isVacation
              ? 'text-amber-700'
              : isRemote
              ? 'text-purple-700'
              : isWorking
              ? 'text-sky-700'
              : isOff
              ? 'text-gray-500'
              : 'text-gray-400';
            const todayRing = isToday ? 'ring-2 ring-sky-500 ring-offset-1' : '';
            const tooltip = `${DAY_LABELS[dayIndex]} ${format(day, 'MMM d')}: ${statusLabel}${
              isOverride ? ' (override)' : ''
            }${hasNote ? `\nNote: ${note}` : ''}`;
            return (
              <div
                key={day.toISOString()}
                className={`relative p-3 border rounded-lg flex flex-col items-start gap-1 ${cellClass} ${todayRing}`}
                aria-label={tooltip}
                title={tooltip}
              >
                {(isOverride || hasNote) && (
                  <div className="absolute top-1 right-1 flex items-center gap-1">
                    {hasNote && (
                      <svg
                        className="w-3 h-3 text-purple-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h6v2H9V8zm-4 4h2v2H5v-2zm4 0h4v2H9v-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {isOverride && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-purple-600"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {DAY_LABELS[dayIndex]}
                  </span>
                  <span
                    className={`text-xs font-medium ${isToday ? 'text-sky-700' : 'text-gray-500'}`}
                  >
                    {format(day, 'M/d')}
                  </span>
                </div>
                <span className={`text-xs font-medium ${statusTextClass}`}>{statusLabel}</span>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && vacationsLoading && (
        <p className="mt-3 text-xs text-gray-400">Refreshing time-off entries…</p>
      )}
    </div>
  );
}

const workingCountLabel = (count) => {
  if (count === 0) return 'No working days scheduled';
  if (count === 1) return '1 working day';
  return `${count} working days`;
};

export default MyWeekScheduleWidget;
